import { Dialog, Portal, PortalProps } from "@ark-ui/react";
import { Box } from "@zenchef/styled-system/jsx";
import Footer, { FooterExposedProps } from "../Footer";
import {
  aromaDrawer,
  AromaDrawerVariantProps,
} from "@zenchef/styled-system/recipes";
import Header, { HeaderExposedProps } from "../Header";
import {
  CSSProperties,
  PropsWithChildren,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { IconName } from "@zenchef/icomoon";
import DrawerCloseTrigger from "./DrawerCloseTrigger";

export interface DrawerHint {
  /**
   * Hint text will shown in the footer/header depending on size variant
   */
  hint?: string;
  /**
   * Icon to show next to the hint
   * @default "help-question-circle"
   */
  hintIcon?: IconName;
}

export interface DrawerProps
  extends Omit<Dialog.RootProps, "id" | "ids">,
    DrawerHint,
    AromaDrawerVariantProps,
    FooterExposedProps,
    HeaderExposedProps,
    PropsWithChildren {
  /**
   * Any react node to use as Trigger to open the drawer
   *(see ControlledDrawer story if trigger should be placed somewhere else)
   */
  trigger?: React.ReactNode;
  /**
   * Z-index of the drawer, the backdrop will be zIndex - 1
   */
  zIndex?: string;
  /**
   *        Whether to show overlay behind the drawer
   * @default true
   */
  onOverlay?: boolean;
  /**
   * By default, the drawer will be rendered at the level of html Body, if you need more control over this, use portal property.
   */
  portal?: PortalProps;

  /**
   * Render content function, if provided, children will be ignored
   * Is useful when you need to render in calling Drawer component as props, like in Filter.Group component
   */
  renderContent?: () => React.ReactNode;
}

const Drawer = ({
  trigger,
  onOverlay = true,
  zIndex = "1000",
  title,
  primaryAction,
  backAction: onBack,
  indicator,
  hint,
  secondaryAction,
  headerAction,
  hintIcon = "help-question-circle",
  titleIcon,
  children,
  portal,
  lazyMount = true,
  unmountOnExit = true,
  trapFocus = false,
  closeOnInteractOutside = onOverlay ?? true,
  renderContent,
  ...props
}: DrawerProps) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const [aromaDrawerVariantProps, restProps] =
    aromaDrawer.splitVariantProps(props);
  const isFullSizeVariant = aromaDrawerVariantProps.size === "full";

  const withHeader = Boolean(
    title ??
      headerAction ??
      onBack ??
      indicator ??
      (isFullSizeVariant && !!hint),
  );
  const withFooter = Boolean(primaryAction ?? secondaryAction);

  const classes = aromaDrawer(aromaDrawerVariantProps);

  const cssVars = {
    "--drawer-z-index": zIndex,
    "--backdrop-z-index": String(Number(zIndex) - 1),
  } as CSSProperties;

  const handleTouchMove = useCallback(
    (moveEvent: TouchEvent, startY: number) => {
      const currentY = moveEvent.touches[0].clientY;
      const deltaY = currentY - startY;

      if (deltaY > 50) {
        restProps.onOpenChange?.({ open: false });
      }
    },
    [restProps.onOpenChange],
  );

  const handleTouchEnd = useCallback(() => {
    document.removeEventListener("touchmove", (e) => handleTouchMove(e, 0));
    document.removeEventListener("touchend", handleTouchEnd);
  }, [handleTouchMove]);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!isFullSizeVariant) return;

      const touch = e.touches[0];
      const startY = touch.clientY;

      document.addEventListener("touchmove", (e) => handleTouchMove(e, startY));
      document.addEventListener("touchend", handleTouchEnd);
    },
    [isFullSizeVariant, handleTouchMove, handleTouchEnd],
  );

  useEffect(() => {
    return () => {
      document.removeEventListener("touchmove", (e) => handleTouchMove(e, 0));
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [handleTouchMove, handleTouchEnd]);

  return (
    <Dialog.Root
      lazyMount={lazyMount}
      unmountOnExit={unmountOnExit}
      trapFocus={trapFocus}
      closeOnInteractOutside={closeOnInteractOutside}
      {...restProps}
    >
      {trigger && <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}
      <Portal {...portal}>
        {onOverlay ? (
          <Dialog.Backdrop className={classes.backdrop} style={cssVars} />
        ) : null}
        <Dialog.Positioner className={classes.positioner} style={cssVars}>
          <Dialog.Content className={classes.content}>
            {withHeader ? (
              <Header
                ref={headerRef}
                onTouchStart={handleTouchStart}
                classes={classes}
                headerAction={headerAction}
                title={title}
                backAction={onBack}
                indicator={indicator}
                hint={isFullSizeVariant ? hint : undefined}
                hintIcon={hintIcon}
                titleIcon={titleIcon}
                closeTrigger={
                  <DrawerCloseTrigger
                    fullWidth={isFullSizeVariant}
                    classes={classes}
                  />
                }
              />
            ) : (
              <DrawerCloseTrigger
                classes={classes}
                fullWidth={isFullSizeVariant}
              />
            )}

            <Box className={classes.body}>
              {renderContent ? renderContent() : children}
            </Box>
            {withFooter ? (
              <Footer
                classes={classes}
                primaryAction={primaryAction}
                secondaryAction={secondaryAction}
                hint={isFullSizeVariant ? undefined : hint}
                hintIcon={hintIcon}
                fullWidth={isFullSizeVariant}
              />
            ) : null}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default Drawer;
